import * as React from 'react';
import { FormikConfig, useFormik } from 'formik';

import type { FormikHandlers, FormikHelpers, FormikState, FormikValues } from 'formik/dist/types';

import { Variant } from '@nebo-team/vobaza.ui.inputs.input-select/dist';

interface IReturnProps<K>
  extends Omit<FormikHandlers, 'handleChange' | 'handleBlur'>,
    FormikHelpers<K>,
    FormikState<K> {
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined, shouldTouch?: boolean) => void;
  handleChange: (e: React.SyntheticEvent<HTMLInputElement>) => void;
  customHandleChange: (name: string, value: string | number | boolean) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleChangeSelect: (name: string, e: Variant | Variant[] | null) => void;
  handleChangeFileInput: (name: string, file: File) => void;
  hasTouched: boolean;
  setHasTouched: (value: boolean) => void;
}

export function useWrappedFormik<Values extends FormikValues = FormikValues>(
  config: FormikConfig<Values>
): IReturnProps<Values> {
  const [hasTouched, setHasTouched] = React.useState(false);

  const formik = useFormik<Values>({
    ...config,
    validateOnBlur: false,
    validateOnChange: false,
  });

  const handleChange = async (e: React.SyntheticEvent<HTMLInputElement, Event>) => {
    const fieldName = e.currentTarget.name;
    try {
      await formik.setFieldValue(fieldName, e.currentTarget.value);
      await formik.setFieldError(fieldName, undefined);
      formik.validateOnChange && (await formik.validateField(fieldName));
      setHasTouched(true);
    } catch (err) {
      console.error(err);
    }
  };

  const customHandleChange = async (name: string, value: number | string | boolean) => {
    try {
      await formik.setFieldValue(name, value);
      formik.validateOnChange && (await formik.validateField(name));
      setHasTouched(true);
    } catch (err) {
      await formik.setFieldError(name, undefined);
    }
  };

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    try {
      await formik.validateField(e.target.name);
    } catch (err) {
      console.error(err);

      await formik.setFieldError(e.target.name, '');
    }
  };

  const handleChangeSelect = async (name: string, value: Variant | Variant[] | null) => {
    await formik.setFieldValue(name, value);
    await formik.setFieldError(name, undefined);
    setHasTouched(true);
  };

  const handleChangeFileInput = async (name: string, file: File) => {
    await formik.setFieldValue(name, file);
    await formik.setFieldError(name, undefined);
    setHasTouched(true);
  };

  const setFieldValue = async (field: string, value: any, shouldValidate?: boolean | undefined, shouldTouch = true) => {
    await formik.setFieldValue(field, value, shouldValidate);
    await formik.setFieldError(field, undefined);
    shouldTouch && setHasTouched(true);
  };

  const resetForm = async (nextState?: Partial<FormikState<Values>>) => {
    await formik.resetForm(nextState);
    setHasTouched(false);
  };

  return {
    ...formik,
    resetForm,
    setFieldValue,
    handleChange,
    customHandleChange,
    handleBlur,
    handleChangeSelect,
    handleChangeFileInput,
    hasTouched,
    setHasTouched,
  };
}
